import React from 'react';
import { VideoActivity } from 'lib/context';
import { theme } from 'lib/style';
import styled, { useTheme } from 'styled-components/macro';
import { Spinner } from 'lib/components';
import dayjs from 'dayjs';
import parse from 'html-react-parser';
import { useCDK } from 'lib/hooks/useCdk';
import { useTranslation } from 'react-i18next';

const ActivityListWrapper = styled.div`
  width: 327px;
  max-height: 50vh;
  overflow-y: auto;
`;
const ActivityList = styled.div<{ color: string }>`
  padding-left: 12px;
  position: relative;
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    background: ${props => props.color};
  }
`;
const ActivityListTitle = styled.div<{ color: string }>`
  color: ${props => props.color};

  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 12px;
`;
const Activity = styled.div<{ color: string }>`
  margin-bottom: 8px;
  position: relative;
  &:before {
    content: '';
    position: absolute;
    left: -18px;
    width: 6px;
    height: 6px;
    border: 4px solid ${props => props.color};
    border-radius: 50%;
    background: ${props => props.color};
  }
`;
const ActivityAction = styled.span<{ color: string }>`
  color: ${props => props.color};
  font-weight: normal;
  font-size: 14px;
  line-height: 16px;
  > b {
    color: ${props => props.color};
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    margin-right: 4px;
  }
`;
const ActivityTime = styled.div`
  color: ${theme.palette.gray60};
  font-weight: normal;
  font-size: 12px;
  line-height: 16px;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

type Props = {
  activities: VideoActivity[];
};
export const VideoActivities = ({ activities = [] }: Props) => {
  const { t } = useTranslation();
  const loading = !activities.length;
  const { isCdkUser } = useCDK();
  const theme = useTheme();

  const color = isCdkUser ? theme.colors.white[100] : theme.colors.black[100];

  return (
    <>
      {!loading ? (
        <ActivityListWrapper>
          <ActivityListTitle color={color}>
            {t('Video Activity')}
          </ActivityListTitle>
          <ActivityList color={color}>
            {activities.map((activity: VideoActivity, index: number) => (
              <Activity color={color} key={`video-activity-${index}`}>
                <ActivityAction color={color}>
                  {parse(activity.activityText || '')}
                </ActivityAction>
                {activity.createdAt && (
                  <ActivityTime>
                    {dayjs(activity.createdAt).format('YYYY-MM-DD hh:mm A')}
                  </ActivityTime>
                )}
              </Activity>
            ))}
          </ActivityList>
        </ActivityListWrapper>
      ) : (
        <SpinnerWrapper>
          <Spinner color={color} />
        </SpinnerWrapper>
      )}
    </>
  );
};
